import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { articles, categoryLabels, categoryPaths, hotArticles } from "@/mocks/articles";
import type { ArticleCategory } from "@/mocks/articles";
import { usePageMeta } from "@/hooks/usePageMeta";
import { formatViews } from "@/utils/format";
import Breadcrumb from "@/components/feature/Breadcrumb";
import Pagination from "@/components/feature/Pagination";

const PAGE_SIZE = 5;
const HISTORY_KEY = "heiliao-search-history";

const typeTabs: { label: string; value: "all" | ArticleCategory }[] = [
  { label: "全部", value: "all" },
  { label: "八卦", value: "gossip" },
  { label: "黑料", value: "black" },
  { label: "新闻", value: "news" },
];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q")?.trim() ?? "";
  const [input, setInput] = useState(query);
  const [type, setType] = useState<"all" | ArticleCategory>("all");
  const [page, setPage] = useState(1);
  const [history, setHistory] = useState<string[]>([]);

  usePageMeta({
    title: query ? `搜索「${query}」- 黑料吃瓜` : "站内搜索 - 黑料吃瓜",
    description: query
      ? `在黑料吃瓜搜索「${query}」的相关娱乐八卦、明星黑料与娱乐新闻，快速找到你要吃的那颗瓜。`
      : "黑料吃瓜站内搜索，支持按关键词模糊查询娱乐八卦、明星黑料与娱乐新闻，并可按内容类型筛选。",
    keywords: `${query || "站内搜索"},搜索,娱乐八卦,明星黑料,娱乐新闻,黑料吃瓜`,
  });

  useEffect(() => {
    setInput(query);
    setPage(1);
  }, [query]);

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(HISTORY_KEY);
      if (stored) {
        setHistory(JSON.parse(stored) as string[]);
      }
    } catch {
      setHistory([]);
    }
  }, []);

  const saveHistory = (keyword: string) => {
    const next = [keyword, ...history.filter((item) => item !== keyword)].slice(0, 5);
    setHistory(next);
    try {
      window.localStorage.setItem(HISTORY_KEY, JSON.stringify(next));
    } catch {
      // 本地存储不可用时忽略
    }
  };

  const submitSearch = (keyword: string) => {
    const trimmed = keyword.trim();
    if (!trimmed) return;
    saveHistory(trimmed);
    setSearchParams({ q: trimmed });
  };

  const results = useMemo(() => {
    if (!query) return [];
    const lower = query.toLowerCase();
    return articles.filter((item) => {
      const text = `${item.title} ${item.excerpt} ${item.tags.join(" ")} ${categoryLabels[item.category]}`.toLowerCase();
      return text.includes(lower) && (type === "all" || item.category === type);
    });
  }, [query, type]);

  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const visible = results.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <div className="max-w-[1320px] mx-auto px-4 md:px-6 py-5 md:py-7">
      <Breadcrumb items={[{ label: "站内搜索" }]} />

      <header className="mt-3 mb-5">
        <h1 className="font-heading text-2xl md:text-[28px] font-black text-foreground-950">站内搜索</h1>
        <p className="text-[13px] text-foreground-500 mt-1.5">支持标题、摘要与标签的关键词模糊匹配。</p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_300px] gap-6">
        <div className="min-w-0">
          <form
            onSubmit={(event) => {
              event.preventDefault();
              submitSearch(input);
            }}
            className="flex items-center gap-2 bg-background-50 border border-background-300 rounded-full pl-4 pr-1.5 py-1.5 focus-within:border-primary-400 transition-colors"
          >
            <i className="ri-search-line text-foreground-400" />
            <input
              value={input}
              onChange={(event) => setInput(event.target.value)}
              type="search"
              placeholder="输入明星姓名、事件或关键词，例如：片酬"
              className="flex-1 min-w-0 bg-transparent outline-none text-sm text-foreground-800 placeholder:text-foreground-400 py-1.5"
            />
            <button
              type="submit"
              className="shrink-0 inline-flex items-center gap-1.5 px-4 h-9 rounded-full bg-primary-500 text-background-50 text-[13px] font-medium whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
            >
              搜索
            </button>
          </form>

          {history.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <span className="text-[12px] text-foreground-400">最近搜索</span>
              {history.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => submitSearch(item)}
                  className="px-2.5 py-1 rounded-full bg-background-100 border border-background-200 text-[12px] text-foreground-600 hover:border-primary-300 hover:text-primary-700 transition-colors cursor-pointer"
                >
                  {item}
                </button>
              ))}
              <button
                type="button"
                onClick={() => {
                  setHistory([]);
                  try {
                    window.localStorage.removeItem(HISTORY_KEY);
                  } catch {
                    // ignore
                  }
                }}
                className="text-[12px] text-foreground-400 hover:text-accent-600 cursor-pointer flex items-center gap-0.5"
              >
                <i className="ri-delete-bin-line" />
                清除
              </button>
            </div>
          )}

          {query ? (
            <div className="mt-5">
              <div className="flex flex-wrap items-center justify-between gap-3 pb-3 border-b border-background-200">
                <p className="text-[13px] text-foreground-600">
                  关键词「<span className="text-primary-700 font-medium">{query}</span>」 找到相关结果{" "}
                  <span className="text-accent-600 font-bold">{results.length}</span> 条
                </p>
                <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200">
                  {typeTabs.map((tab) => (
                    <button
                      key={tab.value}
                      type="button"
                      onClick={() => {
                        setType(tab.value);
                        setPage(1);
                      }}
                      className={`px-3 py-1 rounded-full text-[12.5px] whitespace-nowrap transition-colors cursor-pointer ${
                        type === tab.value
                          ? "bg-background-50 text-foreground-900 font-medium"
                          : "text-foreground-500 hover:text-foreground-800"
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              {visible.length > 0 ? (
                <>
                  <div className="mt-2">
                    {visible.map((article) => (
                      <Link
                        key={article.id}
                        to={`/detail/${article.id}`}
                        className="group flex gap-4 py-4 border-b border-background-200 cursor-pointer"
                      >
                        <div className="w-24 h-20 md:w-36 md:h-24 shrink-0 rounded-md overflow-hidden bg-background-200">
                          <img
                            src={article.cover}
                            alt={article.title}
                            title={`${article.title} - 黑料吃瓜`}
                            className="w-full h-full object-cover object-top group-hover:scale-[1.04] transition-transform duration-500"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 text-[11.5px]">
                            <span className="px-1.5 py-0.5 rounded bg-accent-50 text-accent-700 border border-accent-200">
                              {categoryLabels[article.category]}
                            </span>
                            <span className="text-foreground-400">{article.date}</span>
                          </div>
                          <h2 className="font-heading text-[15.5px] font-bold text-foreground-900 leading-snug mt-1.5 line-clamp-2 group-hover:text-primary-600 transition-colors">
                            {article.title}
                          </h2>
                          <p className="text-[12.5px] text-foreground-500 leading-6 mt-1 line-clamp-2">
                            {article.excerpt}
                          </p>
                        </div>
                      </Link>
                    ))}
                  </div>
                  <Pagination current={currentPage} total={totalPages} onChange={setPage} />
                </>
              ) : (
                <div className="rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-14 text-center mt-5">
                  <i className="ri-search-eye-line text-3xl text-foreground-300" />
                  <p className="font-heading text-base font-bold text-foreground-800 mt-3">没有找到相关内容</p>
                  <p className="text-[13px] text-foreground-500 mt-1.5">
                    换个更短的关键词试试，或者看看下面这些正在被讨论的热门内容。
                  </p>
                  <div className="flex flex-wrap justify-center gap-2 mt-5">
                    {hotArticles.slice(0, 4).map((item) => (
                      <Link
                        key={item.id}
                        to={`/detail/${item.id}`}
                        className="px-3 py-1.5 rounded-md bg-background-50 border border-background-200 text-[12.5px] text-foreground-700 hover:border-primary-300 hover:text-primary-700 transition-colors cursor-pointer max-w-full truncate"
                      >
                        {item.title}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
              <h2 className="font-heading text-lg font-bold text-foreground-900">大家都在搜</h2>
              <p className="text-[13px] text-foreground-500 mt-1.5">点击关键词，直接查看相关报道与回应。</p>
              <div className="flex flex-wrap gap-2 mt-4">
                {["恋情", "片酬", "合约纠纷", "澄清", "综艺剪辑", "音乐节", "税务"].map((keyword) => (
                  <button
                    key={keyword}
                    type="button"
                    onClick={() => submitSearch(keyword)}
                    className="px-3 py-1.5 rounded-md bg-background-100 border border-background-200 text-[12.5px] text-foreground-700 hover:border-accent-300 hover:text-accent-700 transition-colors cursor-pointer"
                  >
                    #{keyword}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6">
                {(Object.keys(categoryLabels) as ArticleCategory[]).map((key) => (
                  <Link
                    key={key}
                    to={categoryPaths[key]}
                    className="flex items-center justify-between gap-3 px-4 py-3 rounded-md bg-background-100 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
                  >
                    <span className="flex items-center gap-2 text-[13.5px] text-foreground-800">
                      <i className="ri-folder-line text-primary-600" />
                      {categoryLabels[key]}
                    </span>
                    <i className="ri-arrow-right-line text-foreground-400" />
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>

        <aside className="flex flex-col gap-5">
          <div className="rounded-lg border border-background-200 bg-background-50 overflow-hidden">
            <div className="px-4 py-3 border-b border-background-200 bg-background-100">
              <h2 className="font-heading text-[15px] font-bold text-foreground-900 flex items-center gap-2">
                <i className="ri-fire-line text-accent-500" />
                热门内容入口
              </h2>
            </div>
            <ol className="divide-y divide-background-200">
              {hotArticles.map((item, index) => (
                <li key={item.id}>
                  <Link
                    to={`/detail/${item.id}`}
                    className="group flex items-start gap-3 px-4 py-3 hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <span
                      className={`w-5 h-5 shrink-0 rounded text-[11px] font-bold flex items-center justify-center mt-0.5 ${
                        index < 3 ? "bg-accent-500 text-primary-950" : "bg-background-200 text-foreground-600"
                      }`}
                    >
                      {index + 1}
                    </span>
                    <span className="min-w-0">
                      <span className="block text-[12.5px] text-foreground-800 leading-6 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {item.title}
                      </span>
                      <span className="text-[11px] text-foreground-400 flex items-center gap-1 mt-0.5">
                        <i className="ri-eye-line" />
                        {formatViews(item.views)}
                      </span>
                    </span>
                  </Link>
                </li>
              ))}
            </ol>
          </div>
        </aside>
      </div>
    </div>
  );
}